import { z } from 'zod';

/** Cursor pagination per MASTER_PROMPT.md §9. Cursor is an opaque, base64url-encoded string. */
export const paginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export function paginatedResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    data: z.array(itemSchema),
    pagination: z.object({
      nextCursor: z.string().nullable(),
      hasMore: z.boolean(),
    }),
  });
}

// Uses btoa/atob (Web Crypto platform APIs available in Workers) rather than Node's Buffer, so
// this runs unmodified in the Workers runtime, Vitest, and later a Node/Postgres host.
function utf8ToBase64Url(input: string): string {
  const bytes = new TextEncoder().encode(input);
  const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join('');
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToUtf8(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeCursor(value: Record<string, unknown>): string {
  return utf8ToBase64Url(JSON.stringify(value));
}

export function decodeCursor<T = Record<string, unknown>>(cursor: string): T {
  return JSON.parse(base64UrlToUtf8(cursor)) as T;
}
